import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { PRIVACY_VERSION } from '../../lib/legal';
import { track, setTelemetryUser } from '../../lib/telemetry';

type Auth = { user: User | null; session: Session | null; loading: boolean; signInWithEmail: (email: string) => Promise<string | null>; verifyCode: (email: string, code: string) => Promise<string | null>; signOut: () => Promise<void> };
const Ctx = createContext<Auth>({ user: null, session: null, loading: false, signInWithEmail: async () => 'Nicht verbunden', verifyCode: async () => 'Nicht verbunden', signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!!supabase);
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setLoading(false); setTelemetryUser(data.session?.user?.id ?? null); });
    const seen = new Set<string>();
    const { data: sub } = supabase.auth.onAuthStateChange((e, s) => {
      setSession(s);
      setTelemetryUser(s?.user?.id ?? null);
      if (e === 'SIGNED_IN' && s?.user && !seen.has(s.user.id)) {
        seen.add(s.user.id);
        // Neues Konto: erste Anmeldung kurz nach dem Anlegen
        const created = Date.parse(s.user.created_at || ''), last = Date.parse(s.user.last_sign_in_at || '');
        const fresh = Number.isFinite(created) && Number.isFinite(last) && Math.abs(last - created) < 2 * 60 * 1000;
        track(fresh ? 'User.registered' : 'User.signedIn', {});
      }
      if (e === 'SIGNED_OUT') track('User.signedOut', {});
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  const signInWithEmail = async (email: string) => {
    if (!supabase) return 'Supabase ist nicht konfiguriert.';
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin, data: { privacy_version: PRIVACY_VERSION } } });
    if (!error) track('User.loginLinkRequested', {});
    return error ? error.message : null;
  };
  /** Anmeldung mit dem 6-stelligen Code aus der E-Mail (statt Link). */
  const verifyCode = async (email: string, code: string) => {
    if (!supabase) return 'Supabase ist nicht konfiguriert.';
    const { error } = await supabase.auth.verifyOtp({ email, token: code.trim(), type: 'email' });
    if (!error) track('User.codeLogin', {});
    return error ? (/expired|invalid/i.test(error.message) ? 'Der Code ist abgelaufen oder falsch. Fordere einen neuen an.' : error.message) : null;
  };
  const signOut = async () => { await supabase?.auth.signOut(); };
  return <Ctx.Provider value={{ user: session?.user ?? null, session, loading, signInWithEmail, verifyCode, signOut }}>{children}</Ctx.Provider>;
}
export const useAuth = () => useContext(Ctx);
